import React from 'react';
import { StickyNote, HardHat, CalendarClock, MapPin, Plus, UserPlus, Pencil, ChevronRight, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import WeatherBadge, { isRoughWeather } from './WeatherBadge';
import { getHolidayName } from '../../utils/holiday-calculator';
import { isWeekend } from '../../utils/date-utils';

const WALNUT = '#8B4513';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const SLATE  = '#3E5C76';
const STONE  = '#8C8275';

function SectionTitle({ children, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '18px 0 8px' }}>
      {children}
      {count != null && (
        <span style={{ background: `${WALNUT}1A`, color: WALNUT, borderRadius: '999px', padding: '0 7px', fontSize: '11px' }}>{count}</span>
      )}
    </div>
  );
}

function ClickRow({ onClick, accent, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        padding: '10px 12px', marginBottom: '6px', textAlign: 'left',
        background: `${accent}12`, border: 'none', borderLeft: `3px solid ${accent}`,
        borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background 0.15s',
      }}
      onMouseOver={e => { e.currentTarget.style.background = `${accent}24`; }}
      onMouseOut={e => { e.currentTarget.style.background = `${accent}12`; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      <ChevronRight size={16} strokeWidth={2} color={STONE} style={{ flexShrink: 0 }} />
    </button>
  );
}

export default function DayDetailModal({
  isOpen, onClose, date, jobs = [], events = [], note, forecast,
  onJobClick, onEventClick, onEditNote, onAddJob, onAssignJob,
}) {
  if (!date) return null;
  const holidayName = getHolidayName(date);
  const crews = [...new Set(jobs.map(j => j.assignedCrew).filter(Boolean))];
  const roughWeather = isRoughWeather(forecast) && jobs.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
      maxWidth="560px"
    >
      {/* Day at a glance */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '-12px' }}>
        {forecast && <WeatherBadge forecast={forecast} showLabel size={15} />}
        {holidayName && <span style={{ fontSize: '12px', fontWeight: '700', color: RUST }}>{holidayName}</span>}
        {isWeekend(date) && !holidayName && <span style={{ fontSize: '12px', color: STONE }}>Weekend</span>}
        {crews.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: STONE }}>
            <HardHat size={13} strokeWidth={2} /> {crews.join(', ')}
          </span>
        )}
      </div>

      {roughWeather && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', padding: '8px 12px', background: `${RUST}12`, borderRadius: '8px', fontSize: '12px', color: RUST, fontWeight: '600' }}>
          <AlertTriangle size={14} strokeWidth={2} /> Rough weather forecast with {jobs.length} job{jobs.length === 1 ? '' : 's'} on the books — worth a check-in with the crew.
        </div>
      )}

      {/* Note */}
      <SectionTitle>Note</SectionTitle>
      <button
        type="button"
        onClick={onEditNote}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%',
          padding: '10px 12px', textAlign: 'left', fontFamily: 'inherit',
          background: note?.text ? `${OCHRE}14` : 'transparent',
          border: note?.text ? 'none' : `1px dashed ${STONE}66`,
          borderLeft: note?.text ? `3px solid ${OCHRE}` : `1px dashed ${STONE}66`,
          borderRadius: '8px', cursor: 'pointer',
          fontSize: '13px', color: note?.text ? OCHRE : STONE,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {note?.text
          ? <><StickyNote size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: '2px' }} /><span style={{ flex: 1 }}>{note.text}</span><Pencil size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: '2px' }} /></>
          : <><Plus size={14} strokeWidth={2} /> Add a note for this day</>}
      </button>

      {/* Jobs */}
      <SectionTitle count={jobs.length}>Jobs</SectionTitle>
      {jobs.length === 0 ? (
        <div style={{ fontSize: '13px', color: STONE }}>Nothing scheduled.</div>
      ) : jobs.map(job => {
        const status = (job.status || 'scheduled').toLowerCase();
        return (
          <ClickRow key={job.id} accent={WALNUT} onClick={() => onJobClick(job)}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {job.contractNumber && <span style={{ color: WALNUT, marginRight: '6px' }}>#{job.contractNumber}</span>}
              {job.customerName || job.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '12px', color: STONE, flexWrap: 'wrap' }}>
              <Badge status={status} style={{ fontSize: '10px', padding: '2px 6px' }}>{status.replace(/_/g, ' ')}</Badge>
              {job.assignedCrew && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}><HardHat size={12} strokeWidth={2} /> {job.assignedCrew}</span>}
              {job.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}><MapPin size={12} strokeWidth={2} /> {job.address}</span>}
            </div>
          </ClickRow>
        );
      })}

      {/* Imported events */}
      {events.length > 0 && (
        <>
          <SectionTitle count={events.length}>Events</SectionTitle>
          {events.map(event => (
            <ClickRow key={event.id} accent={SLATE} onClick={() => onEventClick(event)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                <CalendarClock size={14} strokeWidth={2} color={SLATE} /> {event.title}
              </div>
              <div style={{ marginTop: '3px', fontSize: '12px', color: STONE }}>
                {event.allDay ? 'All day' : event.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                {event.location ? ` · ${event.location}` : ''}
              </div>
            </ClickRow>
          ))}
        </>
      )}

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '22px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="secondary" size="small" onClick={onAssignJob}>
          <UserPlus size={13} strokeWidth={2} style={{ marginRight: '4px', verticalAlign: '-2px' }} /> Assign job here
        </Button>
        <Button size="small" onClick={onAddJob}>
          <Plus size={13} strokeWidth={2} style={{ marginRight: '4px', verticalAlign: '-2px' }} /> Add job on this day
        </Button>
      </div>
    </Modal>
  );
}
